import { useMemo, useState } from "react"
import { Link } from "react-router-dom"
import { useData } from "../data/DataContext"
import type { Objective, ObjectiveStatus, Person } from "../data/types"

type SortField = "personName" | "category" | "year" | "status"
type SortDir = "asc" | "desc"

interface Filters {
  search: string
  team: string
  department: string
  category: string
  year: string
  status: string
  includeDeleted: boolean
}

const EMPTY_FILTERS: Filters = {
  search: "",
  team: "",
  department: "",
  category: "",
  year: "",
  status: "",
  includeDeleted: false,
}

const STATUS_ORDER: Record<ObjectiveStatus, number> = { Active: 0, Draft: 1, Deleted: 2 }

function compareNullable<T extends string | number>(a: T | null, b: T | null): number {
  if (a === b) return 0
  if (a === null) return 1
  if (b === null) return -1
  if (typeof a === "number" && typeof b === "number") return a - b
  return String(a).localeCompare(String(b))
}

function toCsvField(v: unknown): string {
  if (v == null) return ""
  const s = String(v)
  if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`
  return s
}

function exportCsv(
  rows: Array<{ objective: Objective; person: Person | null }>,
): void {
  const headers = [
    "Person",
    "Team",
    "Department",
    "Category",
    "Year",
    "Status",
    "Objective",
    "Measures",
  ]
  const lines = [headers.join(",")]
  for (const { objective: o, person: p } of rows) {
    lines.push(
      [
        toCsvField(o.personName),
        toCsvField(p?.team ?? ""),
        toCsvField(p?.department ?? ""),
        toCsvField(o.category ?? ""),
        toCsvField(o.year ?? ""),
        toCsvField(o.status ?? ""),
        toCsvField(o.objectiveText ?? ""),
        toCsvField(o.measuresText ?? ""),
      ].join(","),
    )
  }
  const csv = "\uFEFF" + lines.join("\r\n")
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" })
  const url = URL.createObjectURL(blob)
  const a = document.createElement("a")
  a.href = url
  const today = new Date().toISOString().slice(0, 10)
  a.download = `nymr-objectives-${today}.csv`
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}

export function ObjectivesPage() {
  const { people, objectives } = useData()

  const peopleByName = useMemo(() => {
    const map = new Map<string, Person>()
    for (const p of people) map.set(p.fullName, p)
    return map
  }, [people])

  const [filters, setFilters] = useState<Filters>(EMPTY_FILTERS)
  const [sort, setSort] = useState<{ field: SortField; dir: SortDir }>({
    field: "personName",
    dir: "asc",
  })

  const categories = useMemo(() => {
    const s = new Set<string>()
    for (const o of objectives) if (o.category) s.add(o.category)
    return [...s].sort()
  }, [objectives])

  const years = useMemo(() => {
    const s = new Set<number>()
    for (const o of objectives) if (o.year != null) s.add(o.year)
    return [...s].sort((a, b) => a - b)
  }, [objectives])

  const departments = useMemo(() => {
    const s = new Set<string>()
    for (const p of people) if (p.department) s.add(p.department)
    return [...s].sort()
  }, [people])

  const rows = useMemo(() => {
    const q = filters.search.trim().toLowerCase()

    const filtered = objectives.filter((o) => {
      if (!filters.includeDeleted && o.status === "Deleted") return false
      const p = peopleByName.get(o.personName) ?? null
      // Exclude Team="User" people entirely from the global table — they’re
      // self-service only and shouldn’t appear in Exec/CLT-facing views.
      if (p?.team === "User") return false
      if (filters.team && p?.team !== filters.team) return false
      if (filters.department && p?.department !== filters.department) return false
      if (filters.category && o.category !== filters.category) return false
      if (filters.year && String(o.year ?? "") !== filters.year) return false
      if (filters.status && o.status !== filters.status) return false
      if (q) {
        const hay = [
          o.personName,
          o.category ?? "",
          o.objectiveText ?? "",
          o.measuresText ?? "",
        ]
          .join(" ")
          .toLowerCase()
        if (!hay.includes(q)) return false
      }
      return true
    })

    const sorted = [...filtered].sort((a, b) => {
      let cmp = 0
      switch (sort.field) {
        case "personName":
          cmp = a.personName.localeCompare(b.personName)
          break
        case "category":
          cmp = compareNullable(a.category, b.category)
          break
        case "year":
          cmp = compareNullable(a.year, b.year)
          break
        case "status": {
          const sa = a.status ? STATUS_ORDER[a.status] : 99
          const sb = b.status ? STATUS_ORDER[b.status] : 99
          cmp = sa - sb
          break
        }
      }
      return sort.dir === "asc" ? cmp : -cmp
    })

    return sorted.map((o) => ({ objective: o, person: peopleByName.get(o.personName) ?? null }))
  }, [objectives, peopleByName, filters, sort])

  const activeFilterCount =
    [filters.team, filters.department, filters.category, filters.year, filters.status].filter(Boolean).length +
    (filters.search ? 1 : 0) +
    (filters.includeDeleted ? 1 : 0)

  const toggleSort = (field: SortField) => {
    setSort((cur) =>
      cur.field === field
        ? { field, dir: cur.dir === "asc" ? "desc" : "asc" }
        : { field, dir: "asc" },
    )
  }

  const reset = () => setFilters(EMPTY_FILTERS)

  return (
    <div>
      {/* Intro row */}
      <div className="mb-[18px] flex flex-wrap items-center justify-between gap-3">
        <p className="text-[14px] leading-[1.6] text-ink-2">
          Showing{" "}
          <span className="font-medium text-ink">
            {rows.length} of {objectives.length}
          </span>{" "}
          objectives. Click any column header to sort.
        </p>
        <button
          type="button"
          onClick={() => exportCsv(rows)}
          disabled={rows.length === 0}
          className="inline-flex items-center gap-2 rounded-sm bg-nymr-navy px-[14px] py-2 text-[12.5px] font-medium text-white transition hover:bg-nymr-navy-dark disabled:cursor-not-allowed disabled:opacity-50"
        >
          <svg viewBox="0 0 20 20" fill="none" className="h-[14px] w-[14px]">
            <path
              d="M10 4v12m-4-4 4 4 4-4M4 18h12"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          Export CSV
        </button>
      </div>

      {/* Filter bar */}
      <div className="mb-5 rounded-lg border border-rule bg-card p-[18px] shadow-card">
        <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-6">
          <FilterField label="Search" span={2}>
            <input
              type="search"
              placeholder="Person, objective, measures…"
              value={filters.search}
              onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value }))}
              className="w-full rounded-sm border border-rule-strong bg-card px-[10px] py-2 text-[13px] text-ink placeholder:text-ink-3 focus:border-nymr-navy focus:outline-none"
            />
          </FilterField>
          <FilterSelect
            label="Team"
            value={filters.team}
            onChange={(v) => setFilters((f) => ({ ...f, team: v }))}
            options={["Exec", "CLT"]}
          />
          <FilterSelect
            label="Department"
            value={filters.department}
            onChange={(v) => setFilters((f) => ({ ...f, department: v }))}
            options={departments}
          />
          <FilterSelect
            label="Category"
            value={filters.category}
            onChange={(v) => setFilters((f) => ({ ...f, category: v }))}
            options={categories}
          />
          <FilterSelect
            label="Year"
            value={filters.year}
            onChange={(v) => setFilters((f) => ({ ...f, year: v }))}
            options={years.map(String)}
          />
        </div>
        <div className="mt-[14px] flex flex-wrap items-end gap-4 border-t border-dashed border-rule pt-[14px]">
          <FilterSelect
            label="Status"
            value={filters.status}
            onChange={(v) => setFilters((f) => ({ ...f, status: v }))}
            options={["Active", "Draft", "Deleted"]}
          />
          <label className="flex items-center gap-2 pb-2 text-[13px] text-ink-2">
            <input
              type="checkbox"
              checked={filters.includeDeleted}
              onChange={(e) =>
                setFilters((f) => ({ ...f, includeDeleted: e.target.checked }))
              }
              className="accent-brass"
            />
            Include deleted
          </label>
          {activeFilterCount > 0 ? (
            <button
              type="button"
              onClick={reset}
              className="ml-auto pb-2 font-mono text-[10.5px] uppercase tracking-[0.12em] text-brass transition hover:text-nymr-navy"
            >
              Clear filters &middot; {activeFilterCount}
            </button>
          ) : null}
        </div>
      </div>

      {/* Table */}
      <div className="overflow-hidden rounded-lg border border-rule bg-card shadow-card">
        <div className="max-h-[70vh] overflow-auto">
          <table className="min-w-full text-[13px]">
            <thead className="sticky top-0 z-10 bg-paper-sunk text-left font-mono text-[10px] uppercase tracking-[0.14em] text-ink-3 shadow-[0_1px_0_0_var(--color-rule)]">
              <tr>
                <SortHeader label="Person" field="personName" sort={sort} onClick={toggleSort} />
                <th className="px-4 py-[13px] font-medium">Team</th>
                <th className="px-4 py-[13px] font-medium">Department</th>
                <SortHeader label="Category" field="category" sort={sort} onClick={toggleSort} />
                <SortHeader label="Year" field="year" sort={sort} onClick={toggleSort} />
                <SortHeader label="Status" field="status" sort={sort} onClick={toggleSort} />
                <th className="px-4 py-[13px] font-medium">Objective</th>
                <th className="px-4 py-[13px] font-medium">Measures</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(({ objective: o, person: p }, idx) => (
                <Row key={o.id} objective={o} person={p} isLast={idx === rows.length - 1} />
              ))}
              {rows.length === 0 ? (
                <tr>
                  <td
                    colSpan={8}
                    className="px-4 py-14 text-center font-mono text-[11px] uppercase tracking-[0.12em] text-ink-3"
                  >
                    No objectives match the current filters
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}

/* ---------- Table row ---------- */

function Row({
  objective: o,
  person: p,
  isLast,
}: {
  objective: Objective
  person: Person | null
  isLast: boolean
}) {
  const border = isLast ? "" : "border-b border-rule"
  return (
    <tr className={`align-top transition hover:bg-paper ${border}`}>
      <td className="whitespace-nowrap px-4 py-[13px] font-medium text-ink">
        {p ? (
          <Link to={`/person/${p.id}`} className="transition hover:text-brass">
            {o.personName}
          </Link>
        ) : (
          o.personName
        )}
      </td>
      <td className="whitespace-nowrap px-4 py-[13px]">
        {p?.team && p.team !== "User" ? (
          <TeamPill team={p.team} />
        ) : (
          <span className="text-ink-3">—</span>
        )}
      </td>
      <td className="whitespace-nowrap px-4 py-[13px] text-ink-2">
        {p?.department ?? <span className="text-ink-3">—</span>}
      </td>
      <td className="whitespace-nowrap px-4 py-[13px]">
        {o.category ? (
          <Link
            to={`/category/${encodeURIComponent(o.category)}`}
            className="font-mono text-[11px] uppercase tracking-[0.1em] text-brass transition hover:text-nymr-navy"
          >
            {o.category}
          </Link>
        ) : (
          <span className="text-ink-3">—</span>
        )}
      </td>
      <td className="whitespace-nowrap px-4 py-[13px] text-ink-2">{o.year ?? "—"}</td>
      <td className="whitespace-nowrap px-4 py-[13px]">
        <StatusBadge status={o.status} />
      </td>
      <td className="min-w-[22rem] whitespace-pre-wrap px-4 py-[13px] text-ink">
        {o.objectiveText ?? ""}
      </td>
      <td className="min-w-[18rem] whitespace-pre-wrap px-4 py-[13px] text-ink-2">
        {o.measuresText ?? ""}
      </td>
    </tr>
  )
}

/* ---------- Filter field + select ---------- */

function FilterField({
  label,
  span,
  children,
}: {
  label: string
  span?: number
  children: React.ReactNode
}) {
  const colSpan = span === 2 ? "lg:col-span-2" : ""
  return (
    <label className={`flex flex-col gap-[6px] ${colSpan}`}>
      <span className="font-mono text-[10px] uppercase tracking-[0.14em] text-ink-3">
        {label}
      </span>
      {children}
    </label>
  )
}

function FilterSelect({
  label,
  value,
  onChange,
  options,
}: {
  label: string
  value: string
  onChange: (v: string) => void
  options: string[]
}) {
  return (
    <FilterField label={label}>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-sm border border-rule-strong bg-card px-[10px] py-2 text-[13px] text-ink focus:border-nymr-navy focus:outline-none"
      >
        <option value="">All</option>
        {options.map((o) => (
          <option key={o} value={o}>
            {o}
          </option>
        ))}
      </select>
    </FilterField>
  )
}

/* ---------- Sort header ---------- */

function SortHeader({
  label,
  field,
  sort,
  onClick,
}: {
  label: string
  field: SortField
  sort: { field: SortField; dir: SortDir }
  onClick: (f: SortField) => void
}) {
  const active = sort.field === field
  const arrow = active ? (sort.dir === "asc" ? "▲" : "▼") : "⇅"
  return (
    <th className="px-4 py-[13px]">
      <button
        type="button"
        onClick={() => onClick(field)}
        className={`inline-flex items-center gap-[6px] font-mono text-[10px] font-medium uppercase tracking-[0.14em] transition ${
          active ? "text-ink" : "text-ink-3 hover:text-ink-2"
        }`}
      >
        {label}
        <span className={active ? "text-brass" : "text-ink-3 opacity-60"}>{arrow}</span>
      </button>
    </th>
  )
}

/* ---------- Status + team pills ---------- */

function StatusBadge({ status }: { status: ObjectiveStatus | null }) {
  if (!status) return <span className="text-ink-3">—</span>
  const cls =
    status === "Active"
      ? "bg-rag-green-bg text-rag-green"
      : status === "Draft"
        ? "bg-rag-amber-bg text-rag-amber-ink"
        : "bg-rag-unset-bg text-ink-3"
  return (
    <span className={`inline-flex items-center rounded-full px-[9px] py-[2px] text-[11px] font-medium ${cls}`}>
      {status}
    </span>
  )
}

function TeamPill({ team }: { team: "Exec" | "CLT" }) {
  const cls =
    team === "Exec"
      ? "bg-[#E8E1FC] text-[#4B3F90]"
      : "bg-[rgb(185_138_58/0.12)] text-brass"
  return (
    <span className={`inline-flex items-center rounded-full px-[7px] py-[1px] text-[10px] font-medium ${cls}`}>
      {team}
    </span>
  )
}
